import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { addAliasAPI, approveAliasAPI, deleteAliasAPI, getAliasesAPI, resolveAliasConflictAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';
import styles from './AliasManager.module.scss';

export default function AliasManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newAlias, setNewAlias] = useState({ alias: '', resolvedUser: '' });
  const [conflictRow, setConflictRow] = useState(null);
  const [pickUser, setPickUser] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAliasesAPI();
      setRows(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { name: 'Alias', selector: (r) => r.alias, grow: 1 },
      {
        name: 'Resolved user',
        cell: (r) => r.resolvedUsers?.length ? '— conflict —' : r.resolvedUser || '—',
      },
      {
        name: 'Source',
        cell: (r) => (
          <span>
            {r.source === 'AI Suggestion' && <Badge bg="primary">AI</Badge>}
            <span className="ms-1">{r.source}</span>
          </span>
        ),
      },
      {
        name: 'Confidence',
        cell: (r) => <Badge bg="secondary">{r.confidence}%</Badge>,
        width: '100px',
      },
      {
        name: 'Status',
        cell: (r) => (
          <Badge bg={r.status === 'conflict' ? 'danger' : r.status === 'pending' ? 'warning' : 'success'}>{r.status}</Badge>
        ),
      },
      {
        name: 'Actions',
        width: '220px',
        cell: (r) => (
          <div className="d-flex flex-wrap gap-1">
            {r.status === 'conflict' ? (
              <Button size="sm" variant="warning" onClick={() => { setConflictRow(r); setPickUser(r.resolvedUsers?.[0] || ''); }}>
                Resolve
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline-success"
                disabled={r.status === 'approved'}
                onClick={async () => {
                  try {
                    await approveAliasAPI(r.id);
                    toast.success('Approved');
                    load();
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
              >
                Approve
              </Button>
            )}
            <Button size="sm" variant="outline-secondary" onClick={() => toast.info('Edit (mock)')}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={async () => {
                try {
                  await deleteAliasAPI(r.id);
                  toast.success('Deleted');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [load]
  );

  if (loading && !hasLoaded) {
    return (
      <div className={styles.page}>
        <PageHeader title="Aliases" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Directory', path: companyUrl('directory') }, { label: 'Aliases' }]} />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div className={styles.page}>
        <PageHeader title="Aliases" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Directory', path: companyUrl('directory') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Alias manager"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Directory', path: companyUrl('directory') },
          { label: 'Aliases' },
        ]}
        primaryAction={{ label: 'Add alias', onClick: () => setAddOpen(true) }}
      />

      {!rows.length ? (
        <EmptyState icon={AlertTriangle} title="No aliases" description="Add one to get started." actionLabel="Add" onAction={() => setAddOpen(true)} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch keyField="id" />
      )}

      <Modal show={addOpen} onHide={() => setAddOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add alias</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control placeholder="alias@domain.com" value={newAlias.alias} onChange={(e) => setNewAlias((s) => ({ ...s, alias: e.target.value }))} className="mb-2" />
          <Form.Control placeholder="Resolved display name" value={newAlias.resolvedUser} onChange={(e) => setNewAlias((s) => ({ ...s, resolvedUser: e.target.value }))} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await addAliasAPI(newAlias);
                toast.success('Added');
                setAddOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(conflictRow)} onHide={() => setConflictRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Resolve conflict</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small">Alias: {conflictRow?.alias}</p>
          {(conflictRow?.resolvedUsers || []).map((uid) => (
            <Form.Check
              key={uid}
              type="radio"
              name="u"
              label={uid}
              checked={pickUser === uid}
              onChange={() => setPickUser(uid)}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await resolveAliasConflictAPI(conflictRow.id, pickUser);
                toast.success('Resolved');
                setConflictRow(null);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Resolve
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
